/**
 * @typedef {{ docId, month, year, lgu }} ReportReference
 * @typedef {{ id, fullname, role }} ReportAuthor
 * @typedef {{ id, name, category }} Attraction
 * @typedef {{ actions: { by: ReportAuthor, action: "submit" | "approve" | "return" | "expire-return" | "publish", by, on, to: "to" | "pto" | "ro", remarks }[] } } ReportHistory
 * @typedef {{ id, name, type, province: { id, name } }} LGU
 * @typedef {{ female: Number, male: Number }} GenderedData
 * @typedef {{ [attractionId: string]: { counts: { domestic: { local: GenderedData, external: GenderedData }, foreign: GenderedData } } }} ReportData
 * @typedef {"partial" | "complete"} ReportCompletionStatus
 * @typedef {"local" | "province" | "region" | "returned-local" | "returned-province" | "published"} ReportCurrentLevel
 * @typedef {{ isNew, id, refId, author: ReportAuthor, lgu: LGU, month, year, dateCreated, data: ReportData, isDraft, completionStatus: ReportCompletionStatus, currentLevel: ReportCurrentLevel, levels, lastUpdated }} Report
 */
import { wait } from "@testing-library/user-event/dist/utils";
import React from "react";
import Client from "../../../Client";
import Exceptions from "../../../Exceptions";
import { listToCollection } from "../../../misc/object";
import { MainRouter } from "../../../routes";
import { approveReport, deleteReport, getReport, getReportDraft, getReportHistory, returnReport, submitReport, unpublishReport, updateReport, updateReportDraft } from "../../../services/api/data/sameday/report";
import { addAttraction, getAttractionList, getLGUList, getProvinceList } from "../../../services/api/meta";
import { AlertDrawerController } from "../../../views/sections/sidebar/AlertDrawer";
import { Button } from "../../../views/widgets/buttons";
import { Dropdown } from "../../../views/widgets/input";
import { showModal } from "../../../views/widgets/modal";
import { ExcelFileImporter } from "../../../views/widgets/modals/import";
import { addOrEditAttraction } from "../../Attractions";
import { Notifier } from "../../conductor";



export class SamedayReportController {

    /** @param {{ isDraft: boolean, ref: ReportReference }} options  */
    constructor(options) {

        this.isDraft = options.isDraft;
        this.reference = options.ref;

        this.notifiers = {
            loading: new Notifier(true),
            loaded: new Notifier(false),
            reloading: new Notifier(false),
            reloaded: new Notifier(false),
            saving: new Notifier(false),
            approving: new Notifier(false),
            submitting: new Notifier(false),
            returning: new Notifier(false),
            unpublishing: new Notifier(false),
            deleting: new Notifier(false),
            addingAttraction: new Notifier(false),
            attractionAdded: new Notifier(false),
            error: new Notifier(null),
            alert: new AlertDrawerController()
        };

        const self = this;
        this.status = {
            get loading() { return self.notifiers.loading.get() },
            get reloading() { return self.notifiers.reloading.get() },
            get saving() { return self.notifiers.saving.get() },
            get approving() { return self.notifiers.approving.get() },
            get submitting() { return self.notifiers.submitting.get() },
            get returning() { return self.notifiers.returning.get() },
            get unpublishing() { return self.notifiers.unpublishing.get() },
            get deleting() { return self.notifiers.deleting.get() },
            get addingAttraction() { return self.notifiers.addingAttraction.get() },
            get error() { return self.notifiers.error.get() },
        };

        /**
         * @type {{ report: Report, attractions: Attraction[], history: ReportHistory }}
         */
        this.data = {};


        this.isUnsaved = false;
    }

    async initialize() {
        Client.ensureSignedIn();
        if(this.isInitialized) return; this.isInitialized = true;

        try {
            this.collection = {
                province: listToCollection(await getProvinceList()),
                lgu: listToCollection(await getLGUList())
            };
            await this.loadData();
            this.notifiers.loaded.update(true);
        } catch(e) {
            // if(e.unauthorized) return this.notifiers.unauthorized.update(true);
            this.notifiers.error.update(e);
            console.error(e);
        }

        this.notifiers.loading.update(false);
    }


    async loadData() {
        let { docId, month, year, lgu } = this.reference;

        if(this.isDraft) {
            this.data.report = await getReportDraft({ month, year, lgu });
        } else {
            this.data.report = await getReport(docId);
            this.data.history = await getReportHistory(docId);
            if(this.data.history) this.data.history.actions.reverse();

            this.reference.lgu = this.data.report.lgu.id;
        }

        await this.loadAttractions();
        this.refreshForm();
    }

    async loadAttractions() {
        const { lgu } = this.reference;
        this.data.attractions = await getAttractionList(lgu || this.data.report.lgu.id);
    }

    async refreshForm(restoreData = false) {
        const oldData = restoreData ? this.collectAttractionsData() : [];
        this.refs = this.data.attractions.map(attraction => ({
            id: attraction.id,
            refs: [ React.createRef(), React.createRef(),
                React.createRef(), React.createRef(),
                React.createRef(), React.createRef() ]
        }));

        const { report } = this.data;

        const { role } = Client.account;
        this.flags = { 
            editable: false, submittable: false, approvable: false, 
            deletable: false, returnable: false, unpublishable: false };

        if(role === "lgu") {
            if(this.isDraft || [ "local", "returned-local" ].includes(report.currentLevel)) {
                this.flags.editable = true;
                this.flags.submittable = true;
                if(!this.isDraft) this.flags.deletable = true;
            } 
        } else if(role === "province") {
            if([ "province", "returned-province" ].includes(report.currentLevel)) {
                this.flags.editable = true;
                this.flags.approvable = true;
                this.flags.returnable = true;
                this.flags.deletable = true;
            }
        } else if([ "admin", "reviewer" ].includes(role)) {
            if([ "region" ].includes(report.currentLevel)) {
                this.flags.editable = true;
                this.flags.approvable = true;
                this.flags.returnable = true;
                this.flags.deletable = true;
            } else if(report.currentLevel === "published") 
                this.flags.unpublishable = true;
        }

        if(restoreData) {
            for(const attractionData of oldData) {
                const { id, counts } = attractionData;
                this.data.report.data[id] = { counts };
            }
        }
    }

    async refresh(closeAlert = true) {
        const { reloading, reloaded, error, alert } = this.notifiers;
        reloading.update(true);
        error.update(null);

        if(closeAlert) alert.close();

        try {
            await this.loadData();
            reloaded.update();
        } catch(e) {
            const semantics = Exceptions.getARESemantics(e);
            alert.alert(semantics.message, { status: "error", title: semantics.title, retry: () => this.refresh() });
        }

        reloading.update(false);
    }





    async import() {
        const controller = {};

        const onExtract = (extracted) => {
            const extras = [];

            console.log(extracted);

            for(const e of extracted) {
                const attraction = this.data.attractions.find(a => a.name === e.name);
                if(attraction) {
                    this.data.report.data[attraction.id] = { counts: e.counts };
                } else extras.push(e);
            }

            controller.close();
            this.refreshForm();
            this.notifiers.reloaded.notify();
            this.notifiers.alert.alert("Data imported to form successfully.");
        };

        await showModal({ 
            title: "Import Excel Sheet", 
            body: <ExcelFileImporter onExtract={onExtract} />,
            footer: []
        }, controller);
    }


    async addAttraction() {
        const { notifiers: { alert, addingAttraction, attractionAdded } } = this;
        const addable = await addOrEditAttraction(null, { 
            lgu: this.reference.lgu, collection: this.collection, lguFixed: true });

        if(addable) {
            addingAttraction.update(true);
            alert.alert("Adding attraction...", { status: "progress" });

            try {
                await addAttraction(addable);
                await this.loadAttractions();
                await this.refreshForm(true);
                attractionAdded.notify();

                alert.alert("Attraction added successfully!");
            } catch(e) {
                const { title, message } = Exceptions.getARESemantics(e);
                alert.alert(message, { title, status: "error" });
            }


            // this.refresh(false);
            addingAttraction.update(false);
        }
    }





    /* ---------------------- SAVING & SUBMITTING ---------------------- */

    collectAttractionsData() {
        return this.refs.map(attraction => {
            const ref = (index) => Number(attraction.refs[index].current.value);
            return {
                id: attraction.id,
                counts: {
                    domestic: { 
                        local: { male: ref(0), female: ref(1) },
                        external: { male: ref(2), female: ref(3) }
                    },
                    foreign: { male: ref(4), female: ref(5) }
                }
            }
        })
    }

    async save(notify = true) {
        const { saving, alert } = this.notifiers;

        if(notify) {
            saving.update(true);
            alert.close();
        }
        

        try {
            const attractions = this.collectAttractionsData();
            const { docId, lgu, month, year } = this.reference;

            const updated = this.isDraft ? 
                await updateReportDraft({ lgu, month, year, attractions }) :
                await updateReport({ docId, attractions });

            this.isUnsaved = false;
            
            if(!this.data.report.id) this.data.report.id = updated.id;
            this.data.report.lastUpdated = updated.lastUpdated;

            if(notify) alert.alert(this.type === "draft" ? "Draft saved successfully." : "Report updated successfully.");
        } catch(e) {
            console.log(e);
            const { title, message } = Exceptions.getARESemantics(e);
            if(notify) alert.alert(message, { title, status: "error", retry: () => this.save() });
        }
        
        if(notify) saving.update(false);
    }

    async submit() {
        const { alert, submitting } = this.notifiers;

        const remarksRef = React.createRef();
        let remarks = "";

        const modalController = {};
        const returned = await showModal({ 
            title: "REPORT SUBMISSION", 
            className: "footer-right",
            body: <div className="alert-submit">
                    <span>Please provide us with a brief analysis/remarks (optional)</span>
                    <textarea ref={remarksRef}></textarea>
                </div>, 
            footer: <Button color="green" icon="save" onClick={() => {
                remarks = remarksRef.current?.value;
                modalController.close(true);
            }}>SUBMIT</Button>,
        }, modalController);

        if(!returned) return;

        submitting.update(true);
        alert.alert("Submitting report...", { status: "progress" });

        try {
            await this.save(false);

            const docId = this.data.report.id;
            if(!docId) return alert.alert("Cannot publish this report yet. Please save it first, then try again.", { status: "error" })

            await submitReport({ docId, remarks });
            MainRouter.back();
        } catch(e) {
            console.error(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.submit() });
        }

        submitting.update(false);
    }

    async approve() {
        const { alert, approving } = this.notifiers;

        const docId = this.data.report.id;
        const remarksRef = React.createRef();
        let remarks = "";

        const modalController = {};
        const returned = await showModal({ 
            title: "REPORT APPROVAL", 
            className: "footer-right",
            body: <div className="alert-approve">
                    <span>Please provide us with a brief analysis/remarks (optional)</span>
                    <textarea ref={remarksRef}></textarea>
                </div>, 
            footer: <Button color="green" icon="save" onClick={() => {
                remarks = remarksRef.current?.value;
                modalController.close(true);
            }}>APPROVE</Button>,
        }, modalController);

        if(!returned) return;

        approving.update(true);
        alert.alert("Approving report...", { status: "progress" });

        try {
            await this.save(false);
            
            await approveReport({ docId, remarks });
            this.isUnsaved = false;
            await this.refresh(false);
            alert.alert("Report approved successfully.");
        } catch(e) {
            console.error(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.approve() });
        }

        approving.update(false);
    }

    async return() {
        const { alert, returning } = this.notifiers;
        const docId = this.data.report.id;

        const targetOptions = [
            { label: "T.O.", value: "to" }
        ];

        if([ "reviewer", "admin" ].includes(Client.account.role))
            targetOptions.unshift({ label: "P.T.O", value: "pto" });


        let remarks = "";
        let currentTarget = targetOptions[0].value;


        const remarksRef = React.createRef();


        const modalController = {};
        const returned = await showModal({ 
            title: "RETURNING REPORT", 
            className: "footer-right",
            body: <div className="alert-return">
                <div className="target">
                    <span>Where to return this report:</span>
                    <Dropdown options={targetOptions} defaultValue={currentTarget} onSelect={(target) => currentTarget = target} />
                </div>
                <span>Please provide us with a brief analysis/remarks (optional)</span>
                <textarea ref={remarksRef}></textarea>
                </div>, 
            footer: <Button color="orange" icon="return" onClick={() => {
                remarks = remarksRef.current?.value;
                modalController.close(true);
            }}>RETURN</Button>,
        }, modalController);

        if(!returned) return;

        returning.update(true);
        alert.alert("Returning report...", { status: "progress" });

        try {
            console.log({ remarks, currentTarget });
            await returnReport({ docId, remarks, target: currentTarget });
            this.isUnsaved = false;
            await this.refresh(false);
            alert.alert("Report returned successfully.");
        } catch(e) {
            console.error(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.return() });
        }

        returning.update(false);
    }

    async unpublish() {
        const { alert, unpublishing } = this.notifiers;

        const docId = this.data.report.id;

        const modalController = {};
        const returned = await showModal({ 
            title: "UNPUBLISHING REPORT", 
            className: "footer-right",
            body: <div className="alert-delete">
                <span>Please confirm that you are about to <b>UNPUBLISH</b> this report.</span>
            </div>, 
            footer: <Button color="orange" icon="return" onClick={() => {
                modalController.close(true);
            }}>UNPUBLISH</Button>,
        }, modalController);

        if(!returned) return;

        unpublishing.update(true);
        alert.alert("Unpublishing report...", { status: "progress" });

        try {
            await unpublishReport({ docId });
            this.isUnsaved = false;
            this.refresh(false);
            alert.alert("Report unpublished successfully.");
        } catch(e) {
            console.error(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.unpublish() });
        }

        unpublishing.update(false);
    }
    

    async delete() {
        const { alert, deleting } = this.notifiers;

        const docId = this.data.report.id;

        const modalController = {};
        const returned = await showModal({ 
            title: "DELETING REPORT",
            titleColor: "red",
            className: "footer-right",
            body: <div className="alert-delete">
                <span>Please confirm that you are about to <b>DELETE</b> this report. This cannot be recovered.</span>
            </div>, 
            footer: <Button color="red" icon="cancel" onClick={() => {
                modalController.close(true);
            }}>DELETE</Button>,
        }, modalController);

        if(!returned) return;

        deleting.update(true);

        try {
            await deleteReport(docId);
            this.isUnsaved = false;
            MainRouter.back();
        } catch(e) {
            console.error(e);
            const { title, message } = Exceptions.getARESemantics(e);
            alert.alert(message, { title, status: "error", retry: () => this.delete() });
        }

        deleting.update(false);
    }

}